import React, { useState, useEffect } from "react"
import Modal from "../../../../components/Modal"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import { getBrandStateLabel } from "../../../../utils/brandStateLabels"
import { defaultStages } from "../../../../static/Brand"
import { TAgetAdmins } from "../../../../services/brandAPI"

const UNASSIGNED_ADMIN_TOKEN = "unassigned"

interface Admin {
  _id: string
  email: string
  name?: string
}

interface FiltersProps {
  isOpen: boolean
  onClose: () => void
  handleFetchData: () => void
  params: {
    min_created_at: Date | null
    max_created_at: Date | null
    min_last_login: Date | null
    max_last_login: Date | null
    brand_name: string
    email: string
    first_name: string
    last_name: string
    active_campaigns: string
    stage: string[]
    brand_state: string[]
    responsible_admin: string[]
    process: {
      meeting_planned: string
      information_mail_sent: string
      directed_to_agency_based_work: string
      demo_campaign_created: string
      balance_loaded: string
      campaign_management_continues: string
      campaign_completed: string
      personal_account: string
    }
    classification: string
    reference: string
  }
  setParams: (params: any) => void
}

const Filters: React.FC<FiltersProps> = ({
  isOpen,
  onClose,
  handleFetchData,
  params,
  setParams,
}) => {
  const [admins, setAdmins] = useState<Admin[]>([])
  const [loadingAdmins, setLoadingAdmins] = useState(false)

  useEffect(() => {
    if (isOpen) {
      fetchAdmins()
    }
  }, [isOpen])

  const fetchAdmins = async () => {
    try {
      setLoadingAdmins(true)
      const response = await TAgetAdmins()
      setAdmins(response)
    } catch (error) {
      console.error("Error fetching admins:", error)
    } finally {
      setLoadingAdmins(false)
    }
  }

  const handleStartDateChange = (date: Date | null) => {
    setParams((prevParams: any) => ({
      ...prevParams,
      min_created_at: date,
    }))
    if (params.max_created_at && date && date > params.max_created_at) {
      setParams((prevParams: any) => ({
        ...prevParams,
        max_created_at: null,
      }))
    }
  }

  const handleEndDateChange = (date: Date | null) => {
    setParams((prevParams: any) => ({
      ...prevParams,
      max_created_at: date,
    }))
  }

  const handleLastLoginStartDateChange = (date: Date | null) => {
    setParams((prevParams: any) => ({
      ...prevParams,
      min_last_login: date,
    }))
    if (params.max_last_login && date && date > params.max_last_login) {
      setParams((prevParams: any) => ({
        ...prevParams,
        max_last_login: null,
      }))
    }
  }

  const handleLastLoginEndDateChange = (date: Date | null) => {
    setParams((prevParams: any) => ({
      ...prevParams,
      max_last_login: date,
    }))
  }

  const handleStageRadioChange = (stage: string) => {
    if (stage === "") {
      setParams({ ...params, stage: [""] })
    } else {
      let stages: string[] = []
      if (params.stage.includes("")) {
        stages = [stage]
      } else {
        const index = params.stage.indexOf(stage)
        if (index !== -1) {
          stages = params.stage.filter((s) => s !== stage)
        } else {
          stages = [...params.stage, stage]
        }
      }
      setParams({ ...params, stage: stages })
    }
  }

  const isChecked = (stage: string) => {
    if (stage === "") {
      return (params.stage || []).includes("")
    } else {
      return (params.stage || []).includes(stage)
    }
  }

  const handleBrandStateRadioChange = (brandState: string) => {
    if (brandState === "") {
      setParams({ ...params, brand_state: [""] })
    } else {
      let brandStates: string[] = []
      if (params.brand_state.includes("")) {
        brandStates = [brandState]
      } else {
        const index = params.brand_state.indexOf(brandState)
        if (index !== -1) {
          brandStates = params.brand_state.filter((s) => s !== brandState)
        } else {
          brandStates = [...params.brand_state, brandState]
        }
      }
      setParams({ ...params, brand_state: brandStates })
    }
  }

  const isBrandStateChecked = (brandState: string) => {
    if (brandState === "") {
      return (params.brand_state || []).includes("")
    } else {
      return (params.brand_state || []).includes(brandState)
    }
  }

  const handleAdminRadioChange = (adminEmail: string) => {
    const currentAdmins = params.responsible_admin || []
    const isSelected = currentAdmins.includes(adminEmail)
    const nextAdmins = isSelected
      ? currentAdmins.filter((s) => s !== adminEmail)
      : [...currentAdmins, adminEmail]

    setParams({ ...params, responsible_admin: nextAdmins })
  }

  const isAdminChecked = (adminEmail: string) => {
    return (params.responsible_admin || []).includes(adminEmail)
  }

  const handleBrandProcessRadioChange = (process: string) => {
    switch (process) {
      case "Toplantı planlandı":
        setParams({
          ...params,
          process: {
            ...params.process,
            meeting_planned: params.process.meeting_planned === "" ? "true" : "",
          },
        })
        break
      case "Bilgilendirme maili iletildi":
        setParams({
          ...params,
          process: {
            ...params.process,
            information_mail_sent: params.process.information_mail_sent === "" ? "true" : "",
          },
        })
        break
      case "Ajans bazlı çalışmaya yönlendirildi":
        setParams({
          ...params,
          process: {
            ...params.process,
            directed_to_agency_based_work:
              params.process.directed_to_agency_based_work === "" ? "true" : "",
          },
        })
        break
      case "Demo kampanya oluşturdu":
        setParams({
          ...params,
          process: {
            ...params.process,
            demo_campaign_created: params.process.demo_campaign_created === "" ? "true" : "",
          },
        })
        break
      case "Bakiye yüklemesi yapıldı":
        setParams({
          ...params,
          process: {
            ...params.process,
            balance_loaded: params.process.balance_loaded === "" ? "true" : "",
          },
        })
        break
      case "Kampanya yönetim süreci devam ediyor":
        setParams({
          ...params,
          process: {
            ...params.process,
            campaign_management_continues:
              params.process.campaign_management_continues === "" ? "true" : "",
          },
        })
        break
      case "Kampanya tamamlandı":
        setParams({
          ...params,
          process: {
            ...params.process,
            campaign_completed: params.process.campaign_completed === "" ? "true" : "",
          },
        })
        break
      case "Kişisel Hesap":
        setParams({
          ...params,
          process: {
            ...params.process,
            personal_account: params.process.personal_account === "" ? "true" : "",
          },
        })
        break
      case "":
        setParams({
          ...params,
          process: {
            ...params.process,
            meeting_planned: "",
            information_mail_sent: "",
            directed_to_agency_based_work: "",
            demo_campaign_created: "",
            balance_loaded: "",
            campaign_management_continues: "",
            campaign_completed: "",
            personal_account: "",
          },
        })
        break
      default:
        break
    }
  }

  const isProcessChecked = (newProcess: string) => {
    switch (newProcess) {
      case "Toplantı planlandı":
        return (params.process?.meeting_planned || "") === "true"
      case "Bilgilendirme maili iletildi":
        return (params.process?.information_mail_sent || "") === "true"
      case "Ajans bazlı çalışmaya yönlendirildi":
        return (params.process?.directed_to_agency_based_work || "") === "true"
      case "Demo kampanya oluşturdu":
        return (params.process?.demo_campaign_created || "") === "true"
      case "Bakiye yüklemesi yapıldı":
        return (params.process?.balance_loaded || "") === "true"
      case "Kampanya yönetim süreci devam ediyor":
        return (params.process?.campaign_management_continues || "") === "true"
      case "Kampanya tamamlandı":
        return (params.process?.campaign_completed || "") === "true"
      case "Kişisel Hesap":
        return (params.process?.personal_account || "") === "true"
      case "":
        return ((params.process as any)?.[newProcess] || "") === "true"
      default:
        return false
    }
  }

  const handleClear = () => {
    setParams({
      min_created_at: null,
      max_created_at: null,
      min_last_login: null,
      max_last_login: null,
      brand_name: "",
      email: "",
      first_name: "",
      last_name: "",
      active_campaigns: "",
      stage: [""],
      brand_state: [""],
      responsible_admin: [],
      process: {
        meeting_planned: "",
        information_mail_sent: "",
        directed_to_agency_based_work: "",
        demo_campaign_created: "",
        balance_loaded: "",
        campaign_management_continues: "",
        campaign_completed: "",
        personal_account: "",
      },
      classification: "",
      reference: "",
    })
  }

  return (
    <>
      {isOpen && (
        <div className="flex w-full items-center justify-center">
          <Modal
            classname="fixed inset-0 bg-gray-800 bg-opacity-50 p-3 flex items-center justify-end z-50"
            classname2="relative bg-white rounded-3xl w-full md:w-1/2 h-full"
            onClick={onClose}
          >
            <div className="w-full p-4 h-full overflow-y-auto overscroll-contain">
              <div className="w-full">
                <h2 className="font-semibold text-2xl">Filtrele</h2>
                <div className="border"></div>
              </div>

              {/* Date Range */}
              <div className="mt-4">
                <h3 className="text-sm font-bold mb-2">Oluşturulma Tarihi Aralığı</h3>
                <div className="flex flex-col gap-3">
                  <DatePicker
                    selected={params.min_created_at}
                    onChange={handleStartDateChange}
                    selectsStart
                    startDate={params.min_created_at}
                    dateFormat="dd/MM/yyyy"
                    endDate={params.max_created_at}
                    className="w-full p-2 border border-gray-200 rounded-md"
                    placeholderText="Başlangıç Tarihi (gg/aa/yyyy)"
                  />
                  <DatePicker
                    selected={params.max_created_at}
                    onChange={handleEndDateChange}
                    selectsEnd
                    startDate={params.min_created_at}
                    dateFormat="dd/MM/yyyy"
                    endDate={params.max_created_at}
                    minDate={params.min_created_at}
                    className="w-full p-2 border border-gray-200 rounded-md"
                    placeholderText="Bitiş Tarihi (gg/aa/yyyy)"
                  />
                </div>
              </div>

              {/* Last Login Date Range */}
              <div className="mt-4">
                <h3 className="text-sm font-bold mb-2">Son Giriş Tarihi Aralığı</h3>

                {/* Quick Filters */}
                <div className="mb-3">
                  <div className="text-xs text-gray-600 mb-2">Hızlı Filtreler:</div>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { label: "Son 1 gün", days: 1 },
                      { label: "Son 3 gün", days: 3 },
                      { label: "Son 1 hafta", days: 7 },
                      { label: "Son 1 ay", days: 30 },
                      { label: "Son 3 ay", days: 90 },
                      { label: "Son 6 ay", days: 180 },
                      { label: "Son 1 yıl", days: 365 },
                    ].map((filter) => (
                      <button
                        key={filter.label}
                        onClick={() => {
                          const endDate = new Date()
                          const startDate = new Date()
                          startDate.setDate(startDate.getDate() - filter.days)
                          setParams((prevParams: any) => ({
                            ...prevParams,
                            min_last_login: startDate,
                            max_last_login: endDate,
                          }))
                        }}
                        className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                      >
                        {filter.label}
                      </button>
                    ))}
                    <button
                      onClick={() => {
                        const startOfYear = new Date(new Date().getFullYear(), 0, 1)
                        const endDate = new Date()
                        setParams((prevParams: any) => ({
                          ...prevParams,
                          min_last_login: startOfYear,
                          max_last_login: endDate,
                        }))
                      }}
                      className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                    >
                      Bu yıl
                    </button>
                    <button
                      onClick={() => {
                        setParams((prevParams: any) => ({
                          ...prevParams,
                          min_last_login: null,
                          max_last_login: null,
                        }))
                      }}
                      className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                    >
                      Temizle
                    </button>
                  </div>
                </div>

                <div className="flex flex-col gap-3">
                  <DatePicker
                    selected={params.min_last_login}
                    onChange={handleLastLoginStartDateChange}
                    selectsStart
                    startDate={params.min_last_login}
                    dateFormat="dd/MM/yyyy"
                    endDate={params.max_last_login}
                    className="w-full p-2 border border-gray-200 rounded-md"
                    placeholderText="Başlangıç Tarihi (gg/aa/yyyy)"
                  />
                  <DatePicker
                    selected={params.max_last_login}
                    onChange={handleLastLoginEndDateChange}
                    selectsEnd
                    startDate={params.min_last_login}
                    dateFormat="dd/MM/yyyy"
                    endDate={params.max_last_login}
                    minDate={params.min_last_login}
                    className="w-full p-2 border border-gray-200 rounded-md"
                    placeholderText="Bitiş Tarihi (gg/aa/yyyy)"
                  />
                </div>
              </div>

              {/* Brand Name Search */}
              <div className="mt-4">
                <h3 className="text-sm font-bold mb-2">Marka İsmi</h3>
                <input
                  type="text"
                  className="w-full p-2 border border-gray-200 rounded-md"
                  placeholder="Marka ismi ara"
                  value={params.brand_name || ""}
                  onChange={(e) => {
                    const text = e.target.value
                    setParams((prevParams: any) => ({
                      ...prevParams,
                      brand_name: text,
                    }))
                  }}
                />
              </div>
              <div className="mt-4">
                <h3 className="text-sm font-bold mb-2">Marka Emaili</h3>
                <input
                  type="text"
                  className="w-full p-2 border border-gray-200 rounded-md"
                  placeholder="Marka Emaili Ara"
                  value={params.email || ""}
                  onChange={(e) => {
                    const text = e.target.value
                    setParams((prevParams: any) => ({
                      ...prevParams,
                      email: text,
                    }))
                  }}
                />
              </div>
              <div className="mt-4">
                <h3 className="text-sm font-bold mb-2">İsim</h3>
                <input
                  type="text"
                  className="w-full p-2 border border-gray-200 rounded-md"
                  placeholder="İsim Ara"
                  value={params.first_name || ""}
                  onChange={(e) => {
                    const text = e.target.value
                    setParams((prevParams: any) => ({
                      ...prevParams,
                      first_name: text,
                    }))
                  }}
                />
              </div>
              <div className="mt-4">
                <h3 className="text-sm font-bold mb-2">Soyisim</h3>
                <input
                  type="text"
                  className="w-full p-2 border border-gray-200 rounded-md"
                  placeholder="Soyisim Ara"
                  value={params.last_name || ""}
                  onChange={(e) => {
                    const text = e.target.value
                    setParams((prevParams: any) => ({
                      ...prevParams,
                      last_name: text,
                    }))
                  }}
                />
              </div>
              <div className="mt-4">
                <h3 className="text-sm font-bold mb-2">Referans</h3>
                <input
                  type="text"
                  className="w-full p-2 border border-gray-200 rounded-md"
                  placeholder="Referans Ara"
                  value={params.reference || ""}
                  onChange={(e) => {
                    const text = e.target.value
                    setParams((prevParams: any) => ({
                      ...prevParams,
                      reference: text,
                    }))
                  }}
                />
              </div>

              {/* Stages */}
              <div className="mt-4">
                <h3 className="text-sm font-bold mb-2">Aktif Kampanya</h3>
                <div className="flex justify-space-between w-1/2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      value={"true"}
                      checked={params.active_campaigns === "true"}
                      onChange={() => setParams({ ...params, active_campaigns: "true" })}
                      className="form-checkbox text-pink-600 mr-2"
                    />
                    <span className="text-sm">Var</span>
                  </label>
                  <label className="flex items-center ml-4">
                    <input
                      type="checkbox"
                      value={"false"}
                      checked={params.active_campaigns === "false"}
                      onChange={() => setParams({ ...params, active_campaigns: "false" })}
                      className="form-checkbox text-pink-600 mr-2"
                    />
                    <span className="text-sm">Yok</span>
                  </label>
                </div>
              </div>

              {/* Stages */}
              <div className="mt-4">
                <h3 className="text-sm font-bold mb-2">Aşamalar</h3>
                <div className="space-y-2">
                  {defaultStages.map((stage) => (
                    <label key={stage.key} className="flex items-center">
                      <input
                        type="checkbox"
                        value={stage.key}
                        checked={isChecked(stage.key)}
                        onChange={() => handleStageRadioChange(stage.key)}
                        className="form-checkbox text-pink-600 mr-2"
                      />
                      <span className="text-sm">{stage.display}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Brand States */}
              <div className="mt-4">
                <h3 className="text-sm font-bold mb-2">Marka Durumu</h3>
                <div className="space-y-2">
                  {[
                    "registered_has_no_campaigns",
                    "campaign_in_draft",
                    "campaign_created_balance_not_loaded",
                    "loaded_balance_has_no_campaigns",
                    "campaign_created_has_no_applications",
                    "campaign_created_balance_loaded_has_no_applications",
                    "campaign_created_received_applications_profile_not_approved",
                    "campaign_created_received_applications_profile_approved",
                    "campaign_created_received_applications_collaboration_completed",
                    "has_balance_loaded",
                    "has_balance_not_loaded",
                    "loaded_balance_multiple_times",
                  ].map((value) => (
                    <label key={value} className="flex items-center">
                      <input
                        type="checkbox"
                        value={value}
                        checked={isBrandStateChecked(value)}
                        onChange={() => handleBrandStateRadioChange(value)}
                        className="form-checkbox text-pink-600 mr-2"
                      />
                      <span className="text-sm">{getBrandStateLabel(value)}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Brand Process */}
              <div className="mt-4">
                <h3 className="text-sm font-bold mb-2">Marka İşlemi</h3>
                <div className="space-y-2">
                  {[
                    "Toplantı planlandı",
                    "Bilgilendirme maili iletildi",
                    "Ajans bazlı çalışmaya yönlendirildi",
                    "Demo kampanya oluşturdu",
                    "Bakiye yüklemesi yapıldı",
                    "Kampanya yönetim süreci devam ediyor",
                    "Kampanya tamamlandı",
                    "Kişisel Hesap",
                  ].map((label) => (
                    <label key={label} className="flex items-center">
                      <input
                        type="checkbox"
                        value={label}
                        checked={isProcessChecked(label)}
                        onChange={() => handleBrandProcessRadioChange(label)}
                        className="form-checkbox text-pink-600 mr-2"
                      />
                      <span className="text-sm">{label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Responsible Admin */}
              <div className="mt-4">
                <h3 className="text-sm font-bold mb-2">Sorumlu Admin</h3>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      value={UNASSIGNED_ADMIN_TOKEN}
                      checked={isAdminChecked(UNASSIGNED_ADMIN_TOKEN)}
                      onChange={() => handleAdminRadioChange(UNASSIGNED_ADMIN_TOKEN)}
                      className="form-checkbox text-pink-600 mr-2"
                    />
                    <span className="text-sm">Sorumlu Admin Yok</span>
                  </label>
                  {loadingAdmins ? (
                    <div className="flex items-center justify-center h-8">
                      <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-blue-600"></div>
                      <span className="text-sm text-gray-500 ml-2">Adminler yükleniyor...</span>
                    </div>
                  ) : (
                    admins.map((admin) => (
                      <label key={admin._id} className="flex items-center">
                        <input
                          type="checkbox"
                          value={admin.email}
                          checked={isAdminChecked(admin.email)}
                          onChange={() => handleAdminRadioChange(admin.email)}
                          className="form-checkbox text-pink-600 mr-2"
                        />
                        <span className="text-sm">{admin.email}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div
                className="flex flex-row justify-between w-full mt-8 py-4 space-x-24"
                style={{
                  boxShadow: "0px -12px 20px -10px rgba(0, 0, 128, 0.1)",
                }}
              >
                <button className="text-blue h-12 rounded-full" onClick={handleClear}>
                  Temizle
                </button>
                <button
                  className="text-blue bg-blue-100 h-12 px-4 rounded-full"
                  onClick={() => {
                    handleFetchData()
                    onClose()
                  }}
                >
                  <span>Uygula</span>
                </button>
              </div>
            </div>
          </Modal>
        </div>
      )}
    </>
  )
}

export default Filters
